import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useRepaymentSubmit } from './useRepaymentSubmit'
import { useAssetBalance } from 'modules/pools/hooks/useAssetBalance'
import { useLoanCalcs } from 'modules/pools/hooks/useLoanCalcs'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'

import { Text } from 'shared/ui/common/Text'
import { InputControl } from 'shared/ui/controls/Input'
import { SelectControl } from 'shared/ui/controls/Select'
// import { ToggleControl } from 'shared/ui/controls/Toggle'
import { InputMaxAction } from 'shared/ui/controls/InputMaxAction'
import { FormSubmitter } from 'shared/ui/common/FormSubmitter'
import { FormTransactionRow } from 'modules/blockChain/ui/FormTransactionRow'
import { Form } from 'shared/ui/controls/FormNetworkGuarded'
import {
  FormLockedValue,
  FormLockedValuesList,
} from 'shared/ui/common/FormLockedValue'
import {
  FormInfoItem,
  FormInfoFrame,
  FormInfoFramesList,
} from 'shared/ui/common/FormInfoFrame'
import { EstimatedGasFee } from 'modules/blockChain/ui/EstimatedGasFee'

import type { Loan } from 'modules/pools/types/Loan'
import type { FormValues, SuccessData } from './types'
import * as formErrors from 'shared/constants/formErrors'
import {
  poolAssetOptions,
  getPoolAssetIcon,
} from 'modules/pools/constants/poolAssets'
import { formatAsset } from 'modules/pools/utils/formatAsset'
import { weiToNum } from 'modules/blockChain/utils/parseWei'
import { bn } from 'shared/utils/bignumber'

import s from './FormRepayment.module.scss'
import { ContractRociPayment } from 'modules/contracts/contracts'
import { protocolPaused } from 'shared/constants/errors'

const depositOptions = [
  poolAssetOptions.USDC,
  // poolAssetOptions.USDT,
  // poolAssetOptions.DAI,
]

const TOTAL_COLLATERAL_AMOUNT = 4444

type Props = {
  loan: Loan
  loanId: string
  onSuccess: (successData: SuccessData) => void
}

export function FormRepayment({ loan, loanId, onSuccess }: Props) {
  const { isChainUnsupported } = useWeb3()
  const formMethods = useForm<FormValues>({
    shouldUnregister: false,
    defaultValues: {
      borrowedAsset: '',
      amount: '',
      returnCollateral: false,
      collateralAmount: '',
    },
  })

  const { setValue, trigger, getValues } = formMethods

  const { totalDebt, borrowedAsset, borrowedAssetDecimals } = useLoanCalcs({
    loan,
  })
  const { data: balanceWei } = useAssetBalance(borrowedAsset)
  const balance =
    balanceWei && borrowedAsset && weiToNum(balanceWei, borrowedAssetDecimals)

  const { data: isContractPaused } = ContractRociPayment.useSwrRpc(
    'paused',
    [],
    { refreshInterval: 15000 },
  )

  useEffect(() => {
    if (borrowedAsset) {
      setValue('borrowedAsset', borrowedAsset)
    }
  }, [borrowedAsset, setValue])

  const {
    submit,
    txAllowance,
    isSubmitting,
    isLocked,
    estimatedGas,
    unlockForm,
  } = useRepaymentSubmit({
    loanId,
    onSuccess,
    formMethods,
  })

  /*
    This useEffect triggers every time
    some of the deps of amount.validate func changes.
    That way we handle validation not only on value change, but also
    on every one of the deps change.
  */
  useEffect(() => {
    if (getValues('amount') !== '') {
      setTimeout(() => trigger('amount'), 10)
    }
  }, [
    trigger,
    getValues,
    balanceWei,
    borrowedAsset,
    borrowedAssetDecimals,
    totalDebt,
  ])

  const handleClickMaxAmount = useCallback(() => {
    if (!balance) return
    setValue('amount', String(Math.min(totalDebt, balance)))
  }, [setValue, totalDebt, balance])

  const handleClickMaxCollateralAmount = useCallback(() => {
    setValue('collateralAmount', String(TOTAL_COLLATERAL_AMOUNT))
  }, [setValue])
  const returnCollateral = formMethods.watch('returnCollateral')

  return (
    <Form formMethods={formMethods} onSubmit={submit}>
      {isContractPaused && (
        <Text isCentered color="error" className={s.errorMessage}>
          {protocolPaused}
        </Text>
      )}
      {!isLocked && (
        <>
          <SelectControl
            readOnly
            name="borrowedAsset"
            concat="bottom"
            placeholder="Asset"
            options={depositOptions}
            rules={{ required: formErrors.required }}
          />

          <InputControl
            name="amount"
            concat="top"
            placeholder="Amount"
            onlyNumber
            rules={{
              required: formErrors.required,
              validate: (val: string) =>
                formErrors.weiParsing(val, borrowedAssetDecimals) ||
                formErrors.notMore(val, totalDebt, borrowedAsset) ||
                formErrors.balance(
                  val,
                  balanceWei,
                  borrowedAsset,
                  borrowedAssetDecimals,
                ) ||
                true,
            }}
            action={<InputMaxAction onClick={handleClickMaxAmount} />}
            readOnly={isChainUnsupported}
          />

          {/* TODO: develop ux for this kind of chained transaction */}
          {/* <ToggleControl label="Return collateral" name="returnCollateral" /> */}

          {returnCollateral && (
            <div className={s.inputWithHintWrap}>
              <InputControl
                name="collateralAmount"
                placeholder="Amount of collateral"
                onlyNumber
                withMargin={false}
                className={s.inputWithHint}
                rules={{
                  required: formErrors.required,
                  validate: (val: string) =>
                    formErrors.notMore(val, TOTAL_COLLATERAL_AMOUNT) || true,
                }}
                action={
                  <InputMaxAction onClick={handleClickMaxCollateralAmount} />
                }
              />
              <div>
                <Text tag="span" size={14} weight={500} color="secondary">
                  Current USD value:{' '}
                </Text>
                <Text tag="span" size={14} weight={500} color="default">
                  2720 USD
                </Text>
              </div>
            </div>
          )}
        </>
      )}

      {isLocked && (
        <FormLockedValuesList>
          <FormLockedValue
            label="Borrowed asset"
            name="borrowedAsset"
            getIcon={getPoolAssetIcon}
          />
          <FormLockedValue
            label="Amount"
            name="amount"
            showTooltipUnformatted
            formatValue={v => formatAsset(v, borrowedAsset)}
          />
          {returnCollateral && (
            <FormLockedValue
              label="Amount of collateral"
              name="collateralAmount"
              subvalue="323 USD"
            />
          )}
        </FormLockedValuesList>
      )}

      <FormInfoFramesList>
        <FormInfoFrame>
          <FormInfoItem
            label="Total debt"
            value={formatAsset(totalDebt, borrowedAsset)}
            sign={borrowedAsset}
            tooltip={totalDebt}
          />
          <FormInfoItem
            label="Your balance"
            value={formatAsset(balance, borrowedAsset)}
            sign={borrowedAsset}
            tooltip={balance && new bn(balance).toString()}
          />
        </FormInfoFrame>
      </FormInfoFramesList>

      {isLocked && (
        <div>
          <FormTransactionRow
            label="Token spending approval"
            tx={txAllowance}
          />
        </div>
      )}

      {isLocked && estimatedGas && <EstimatedGasFee value={estimatedGas} />}

      <FormSubmitter
        isSubmitting={isSubmitting}
        isLocked={isLocked}
        isDisabled={isContractPaused || isChainUnsupported}
        firstStepText="Repay"
        onClickUnlock={unlockForm}
        submitTestId="repayment-form-submit-button"
        confirmTestId="repayment-form-confirm-button"
      />
    </Form>
  )
}
